'use client'

import mergeRefs from 'merge-refs'
import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Transition, TransitionStatus } from 'react-transition-group'

const transitionStyles = {
  entering: {
    opacity: 1,
    transform: 'none',
    visibility: 'visible',
    transition:
      'opacity 210ms cubic-bezier(0.4, 0, 0.2, 1) 90ms, transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
  entered: {
    opacity: 1,
    transform: 'none',
    visibility: 'visible',
  },
  exiting: {
    visibility: 'visible',
    transition:
      'opacity 90ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
} as Record<TransitionStatus, React.CSSProperties>

interface CascadeContextValue {
  container: HTMLDivElement | null
  currentLevel: number
  level: number
  next: () => void
  previous: () => void
}

const CascadeContext = createContext<CascadeContextValue>(
  {} as CascadeContextValue
)

export interface CascadeProps extends React.HTMLAttributes<HTMLDivElement> {}

export const Cascade = forwardRef(function (
  props: CascadeProps,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const { children, ...other } = props

  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const [currentLevel, setCurrentLevel] = useState(0)

  const next = useCallback(() => {
    setCurrentLevel((prev) => prev + 1)
  }, [])

  const previous = useCallback(() => {
    setCurrentLevel((prev) => prev - 1)
  }, [])

  const contextValue = useMemo(
    () => ({
      container,
      currentLevel,
      level: 0,
      next,
      previous,
    }),
    [container, currentLevel, next, previous]
  )

  return (
    <CascadeContext.Provider value={contextValue}>
      <div ref={setContainer} {...other}>
        {children}
      </div>
    </CascadeContext.Provider>
  )
})

export interface CascadeContentProps
  extends React.HTMLAttributes<HTMLDivElement> {}

export const CascadeContent = forwardRef(function CascadeContent(
  props: CascadeContentProps,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const { children, style, ...other } = props

  const nodeRef = useRef<HTMLDivElement>(null)

  const { currentLevel } = useContext(CascadeContext)

  return (
    <Transition nodeRef={nodeRef} in={currentLevel === 0} timeout={400}>
      {(status) => (
        <div
          ref={nodeRef}
          style={{
            ...style,
            position: 'absolute',
            inset: 0,
            opacity: 0,
            transform:
              currentLevel > 0 ? 'translateX(-120px)' : 'translateX(120px)',
            visibility: 'hidden',
            ...transitionStyles[status],
          }}
          {...other}
        >
          {children}
        </div>
      )}
    </Transition>
  )
})

interface CascadeSubContextValue {
  open: boolean
  setOpen: (value: boolean) => void
}

const CascadeSubContext = createContext<CascadeSubContextValue>(
  {} as CascadeSubContextValue
)

export interface CascadeSubProps {
  children?: React.ReactNode
}

export function CascadeSub(props: CascadeSubProps) {
  const { children } = props
  const [open, setOpen] = useState(false)

  const context = useContext(CascadeContext)

  const newContextValue = useMemo(
    () => ({
      ...context,
      level: context.level + 1,
    }),
    [context]
  )

  const subContextValue = useMemo(
    () => ({
      open,
      setOpen,
    }),
    [open]
  )

  return (
    <CascadeContext.Provider value={newContextValue}>
      <CascadeSubContext.Provider value={subContextValue}>
        {children}
      </CascadeSubContext.Provider>
    </CascadeContext.Provider>
  )
}

export interface CascadeSubTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

export const CascadeSubTrigger = forwardRef(function CascadeSubTrigger(
  props: CascadeSubTriggerProps,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  const { children, onClick, ...other } = props
  const { next } = useContext(CascadeContext)
  const { setOpen } = useContext(CascadeSubContext)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(event)

      if (event.defaultPrevented) {
        return
      }
    }

    next()
    setOpen(true)
  }

  return (
    <button ref={ref} onClick={handleClick} {...other}>
      {children}
    </button>
  )
})

export interface CascadeSubContentProps
  extends React.HTMLAttributes<HTMLDivElement> {}

export const CascadeSubContent = forwardRef(function CascadeSubContent(
  props: CascadeSubContentProps,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const { children, style, ...other } = props
  const nodeRef = useRef<HTMLDivElement>(null)
  const { level, currentLevel, container } = useContext(CascadeContext)
  const { open } = useContext(CascadeSubContext)

  if (!container) {
    return null
  }

  return createPortal(
    <Transition
      nodeRef={nodeRef}
      in={open && currentLevel === level}
      timeout={400}
      // mountOnEnter
      // unmountOnExit={!open}
    >
      {(status) => (
        <div
          ref={mergeRefs(ref, nodeRef)}
          style={{
            ...style,
            position: 'absolute',
            inset: 0,
            opacity: 0,
            transform:
              currentLevel > level ? 'translateX(-120px)' : 'translateX(120px)',
            visibility: 'hidden',
            ...transitionStyles[status],
          }}
          {...other}
        >
          {children}
        </div>
      )}
    </Transition>,
    container
  )
})

export interface CascadeBackProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode
}

export const CascadeBack = forwardRef(function CascadeBack(
  props: CascadeBackProps,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  const { children, onClick, ...other } = props
  const { previous } = useContext(CascadeContext)
  const { setOpen } = useContext(CascadeSubContext)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(event)

      if (event.defaultPrevented) {
        return
      }
    }

    previous()
    setOpen(false)
  }

  return (
    <button ref={ref} onClick={handleClick} {...other}>
      {children}
    </button>
  )
})
